import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { playerStatKeys } from '@/hooks/player/playerStatsKeys';
import { TeamPlayerAvailabilityPayload } from '@/infrastructure/network/dto';
import { addTeamPlayerAvailability, removeTeamPlayerAvailability } from '@/services/actions/teamLeagueTeamPlayersActions';

interface UpdatePlayerActivityParams {
	organizationId: string;
	teamLeagueId: string;
	seasonId: string;
	playerId: string;
}
export const useUpdatePlayerActivityMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			data: UpdatePlayerActivityParams & {
				teamLeagueTeamPlayersAvailabilities: TeamPlayerAvailabilityPayload;
			}
		) => {
			try {
				const apiResponse = await addTeamPlayerAvailability({
					organizationUuid: data.organizationId,
					teamLeagueUuid: data.teamLeagueId,
					playerUuid: data.playerId,
					body: data.teamLeagueTeamPlayersAvailabilities
				});

				if (!apiResponse.success) {
					throw new Error('Failed to update player activity.');
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data, { teamLeagueId, organizationId, playerId }) => {
			queryClient.invalidateQueries({
				queryKey: playerStatKeys.playerAvailability(organizationId, teamLeagueId, playerId)
			});
			toast.success('Player activity updated successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};
interface RemovePlayerActivityParams {
	organizationId: string;
	teamLeagueId: string;
	playerId: string;
	availabilityUuids: string;
}

export const useRemovePlayerAvailabilityMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: RemovePlayerActivityParams) => {
			try {
				const apiResponse = await removeTeamPlayerAvailability({
					organizationUuid: data.organizationId,
					teamLeagueUuid: data.teamLeagueId,
					playerUuid: data.playerId,
					availabilityUuids: data.availabilityUuids
				});

				if (!apiResponse.success) {
					throw new Error('Failed to remove player availability.');
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data, { teamLeagueId, organizationId, playerId }) => {
			queryClient.invalidateQueries({
				queryKey: playerStatKeys.playerAvailability(organizationId, teamLeagueId, playerId)
			});
			toast.success('Player availability removed successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};
